import { alpha, Box } from '@mui/material';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { FC, useMemo } from 'react';
import { Bar, Line } from 'react-chartjs-2';

import useAppTheme from '../../../../hooks/useAppTheme';
import { ChartSpec } from '../../interfaces';
import { formatTick, formatValue } from '../../utils/format';

ChartJS.register(CategoryScale, LinearScale, BarController, BarElement, LineController, LineElement, PointElement, Tooltip, Legend, Filler);

interface InsightChartProps {
  spec: ChartSpec;
  height?: number;
}

// Renders an insight's chart spec. The first series is the subject (brand colour); a second series is a
// comparison (muted). Highlighted x values are drawn in the strong shade.
const InsightChart: FC<InsightChartProps> = ({ spec, height = 260 }) => {
  const { palette } = useAppTheme();
  const subject = palette.primary.main;
  const comparison = palette.grey[400];
  const format = spec.series[0]?.format ?? 'count';
  const labels = useMemo(() => spec.rows.map((r) => String(r[spec.x.key] ?? '')), [spec]);
  const horizontal = spec.type === 'horizontalBar';

  const datasets = useMemo(() => {
    const highlight = new Set((spec.highlight ?? []).map(String));
    // A series with no values at all (a target never set) is left out, legend included.
    const shown = spec.series.filter((s) => spec.rows.some((r) => r[s.key] !== null && r[s.key] !== undefined));
    return shown.map((s, i) => {
    const colour = i === 0 ? subject : comparison;
    // A line's place counts the series other than faded ones, so adding a forecast keeps last year grey and the target amber.
    const place = i - shown.slice(0, i).filter((x) => x.faded).length;
    const data = spec.rows.map((r) => (r[s.key] === null || r[s.key] === undefined ? null : Number(r[s.key])));
    if (spec.type === 'line') {
      return {
        label: s.label, data, borderColor: colour, backgroundColor: alpha(colour, i === 0 ? 0.12 : 0),
        fill: i === 0, pointRadius: labels.length > 40 ? 0 : 3, pointHoverRadius: 6, borderWidth: i === 0 ? 2 : 1.5,
        borderDash: i === 0 ? [] : [4, 4], tension: 0.25,
      };
    }
    // A line over bars (combo): e.g. the month's target or last year over actual sales.
    if (s.kind === 'line') {
      const lineColour = place >= 2 ? palette.warning.main : comparison;
      return {
        type: 'line' as const, label: s.label, data, borderColor: lineColour, backgroundColor: lineColour, borderWidth: 2,
        borderDash: place >= 2 ? [] : [4, 4], pointRadius: 3, tension: 0.2, spanGaps: true, order: 0,
      };
    }
    // A faded series (a forecast) is a pale bar in the subject's colour; empty bars take no room beside the others.
    if (s.faded) {
      return { label: s.label, data, borderRadius: 6, maxBarThickness: 36, order: 1, skipNull: true,
        backgroundColor: alpha(subject, 0.22), borderColor: alpha(subject, 0.7), borderWidth: 1 };
    }
    return {
      label: s.label, data, borderRadius: 6, maxBarThickness: 36, order: 1, skipNull: true,
      backgroundColor: labels.map((l) => (i === 0 ? (highlight.size && !highlight.has(l) ? alpha(subject, 0.45) : subject) : alpha(comparison, 0.7))),
    };
    });
  }, [spec, subject, comparison, labels, palette.warning.main]);

  const options: ChartOptions<'bar' | 'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? 'y' : 'x',
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: spec.series.length > 1, position: 'bottom', labels: { usePointStyle: true, boxWidth: 8, color: palette.text.secondary } },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${formatValue(ctx.parsed[horizontal ? 'x' : 'y'] as number, spec.series.find((x) => x.label === ctx.dataset.label)?.format ?? format)}`,
        },
      },
    },
    scales: {
      [horizontal ? 'y' : 'x']: {
        grid: { display: false },
        ticks: { color: palette.text.secondary, autoSkip: true, maxRotation: 0, maxTicksLimit: horizontal ? 12 : 10 },
      },
      [horizontal ? 'x' : 'y']: {
        beginAtZero: spec.zero !== false,
        grace: spec.zero === false ? '10%' : 0,
        grid: { color: alpha(palette.text.primary, 0.06) },
        border: { display: false },
        ticks: { color: palette.text.secondary, callback: (v) => formatTick(Number(v), format), maxTicksLimit: 6 },
      },
    },
  } as ChartOptions<'bar' | 'line'>;

  return (
    <Box sx={{ height, position: 'relative' }}>
      {spec.type === 'line'
        ? <Line data={{ labels, datasets: datasets as any }} options={options as ChartOptions<'line'>} />
        : <Bar data={{ labels, datasets: datasets as any }} options={options as ChartOptions<'bar'>} />}
    </Box>
  );
};

export default InsightChart;
